import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Menu, X, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useState, useEffect } from "react";
import Logo from "@/components/Logo";

const menuLink = "block py-4 font-heading uppercase tracking-[0.3em] text-2xl text-white/70 hover:text-[#FF3333] transition-colors border-b border-white/5";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll when overlay is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <>
      <header className="sticky top-0 z-50 backdrop-blur-md bg-black/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group text-white" data-testid="navbar-logo">
            <Logo size={28} />
            <div className="hidden sm:block">
              <div className="font-heading text-lg leading-none tracking-[0.25em]">FORGED</div>
              <div className="font-mono text-[9px] tracking-[0.4em] text-white/40 mt-0.5">ATHLETICS</div>
            </div>
          </Link>

          <div className="flex items-center gap-4">
            {!user && (
              <Link
                to="/auth"
                data-testid="navbar-login"
                className="font-mono text-[10px] tracking-[0.4em] uppercase text-white/70 hover:text-[#FF3333] transition-colors"
              >
                Log In
              </Link>
            )}
            {user && (
              <Link
                to="/dashboard"
                data-testid="navbar-dashboard"
                className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.4em] uppercase text-white/70 hover:text-[#FF3333] transition-colors"
              >
                <UserIcon className="w-3.5 h-3.5" />
                {user.name?.split(" ")[0] || "Account"}
              </Link>
            )}
            <button
              onClick={() => setOpen(true)}
              data-testid="navbar-menu-toggle"
              aria-label="Open menu"
              className="inline-flex items-center gap-2 text-white/80 hover:text-[#FF3333] transition-colors"
            >
              <span className="hidden sm:block font-mono text-[10px] tracking-[0.4em] uppercase">Menu</span>
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </div>
        </div>
        {/* subtle red divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-[#FF3333]/40 to-transparent" />
      </header>

      {/* OVERLAY MENU */}
      {open && (
        <div
          className="fixed inset-0 z-[60] bg-black animate-[fadeIn_0.2s_ease-out]"
          data-testid="navbar-overlay-menu"
        >
          <div className="h-16 px-6 md:px-10 max-w-7xl mx-auto flex items-center justify-between border-b border-white/5">
            <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-3 text-white">
              <Logo size={28} />
              <div className="hidden sm:block">
                <div className="font-heading text-lg leading-none tracking-[0.25em]">FORGED</div>
                <div className="font-mono text-[9px] tracking-[0.4em] text-white/40 mt-0.5">ATHLETICS</div>
              </div>
            </Link>
            <button
              onClick={() => setOpen(false)}
              data-testid="navbar-menu-close"
              aria-label="Close menu"
              className="inline-flex items-center gap-2 text-white/80 hover:text-[#FF3333] transition-colors"
            >
              <span className="hidden sm:block font-mono text-[10px] tracking-[0.4em] uppercase">Close</span>
              <X className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-[#FF3333]/40 to-transparent" />

          <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
            <div className="font-mono text-[10px] tracking-[0.5em] text-[#FF3333] mb-8">
              NAVIGATE THE FORGE
            </div>

            <nav className="space-y-0">
              <NavLink to="/pricing" data-testid="nav-pricing" className={menuLink}>
                Pricing
              </NavLink>
              <NavLink to="/questionnaire" data-testid="nav-plan" className={menuLink}>
                AI Plan
              </NavLink>
              {user && (
                <>
                  <NavLink to="/dashboard" data-testid="nav-dashboard" className={menuLink}>
                    Dashboard
                  </NavLink>
                  <NavLink to="/track/nutrition" data-testid="nav-track-nutrition" className={menuLink}>
                    Macros
                  </NavLink>
                  <NavLink to="/track/workouts" data-testid="nav-track-workouts" className={menuLink}>
                    Lifts
                  </NavLink>
                </>
              )}
              <NavLink to="/coming-soon" data-testid="nav-coming-soon" className={menuLink}>
                Waifu Collection <span className="text-[#FF3333] text-xs font-mono tracking-widest ml-3">SOON</span>
              </NavLink>
              <NavLink to="/legal" data-testid="nav-legal" className={menuLink}>
                Disclaimer
              </NavLink>
            </nav>

            <div className="mt-12 pt-8 border-t border-white/10 flex items-center justify-between flex-wrap gap-4">
              {user ? (
                <>
                  <div className="flex items-center gap-3">
                    <UserIcon className="w-4 h-4 text-[#FF3333]" />
                    <span className="font-mono text-xs tracking-widest text-white/70" data-testid="nav-user-name">
                      {user.name}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    data-testid="nav-logout"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white/70 font-heading uppercase tracking-[0.3em] text-xs hover:border-[#FF3333] hover:text-[#FF3333] transition-colors"
                  >
                    <LogOut className="w-3.5 h-3.5" /> Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/auth"
                  data-testid="nav-login"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-2 px-8 py-3 border border-[#FF3333] text-[#FF3333] font-heading uppercase tracking-[0.3em] text-xs hover:bg-[#FF3333] hover:text-black transition-colors"
                >
                  <UserIcon className="w-3.5 h-3.5" /> Login / Sign Up
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
}
